<script lang="ts" setup generic="T">
	import type { UploadTypeResponse } from '@/components/Shared/upload-input.vue'
	import type { CsvObjectContext } from '@/composables/useCsvToArray'

	export type BatchUploadSteps = {
		[id: string]: {
			desc: string
			key: 'start' | 'details' | 'upload'
		}
	}

	export type BatchResult = {
		file: UploadTypeResponse
	}
	const props = defineProps<{
		file?: File | null
		downloading?: boolean
		errorIntercept?: boolean
		stepGuide: BatchUploadSteps
		previewImgSrc: string
		uploadFailed?: boolean
		reUploadMessage?: string
		fileFormat?: string
	}>()
	const emits = defineEmits<{
		(e: 'uploaded', arg: BatchResult): void
		(e: 'update:file', arg: File | null): void
		(e: 'change', arg?: File | null): void
		(e: 'download'): void
	}>()
	const reUpload = shallowRef(false)
	const isPreviewingImage = shallowRef(false)
	const failed = shallowRef(false)
	const isCsvError = shallowRef(false)
	const csvFile = ref<UploadTypeResponse | null>(null)
	const errorMessage = ref<CsvObjectContext['error'] | null>(null)

	const initFile = (fileResponse: UploadTypeResponse) => {
		failed.value = false
		if (fileResponse.file && fileResponse.file.type.includes('.spreadsheetml.sheet')) {
			csvFile.value = fileResponse
			emits('update:file', fileResponse.file || null)
			emits('change', fileResponse?.file || null)
			window.scrollTo({
				top: 0,
				behavior: 'smooth'
			})
			return null
		}

		useNotification().alert({
			status: 500,
			message: `${fileResponse.file.name} file not a supported type`
		})
	}

	const previewImage = (value: boolean) => {
		isPreviewingImage.value = value
	}
</script>

<template>
	<div class="mx-auto mt-16 w-full md:mt-0 md:bg-white md:p-8">
		<uploads-reupload-file
			v-if="failed || uploadFailed || reUpload"
			:accept="fileFormat || '.xlsx'"
			:is-document="true"
			:note="reUploadMessage || ''"
			@init="initFile"
		/>
		<div v-else class="flex flex-col">
			<div v-for="(value, k, i) in stepGuide" :key="k" class="line mb-16 flex items-start space-x-3 sm:space-x-5">
				<div class="relative z-10 hidden flex-none sm:block">
					<span class="rounded-full bg-blue-950 px-5 py-4 text-lg font-bold text-white">
						{{ i + 1 }}
					</span>
				</div>

				<div class="flex-1">
					<h1 class="mb-2 text-lg font-medium capitalize text-gray-600">
						{{ k }}
					</h1>
					<p class="mb-8 mt-0 max-w-[554px] pt-0 text-sm text-gray-500" v-html="value.desc || ''" />
					<template v-if="value.key === 'start'">
						<div
							class="flex min-h-[80px] w-full flex-wrap items-center justify-between gap-5 rounded-lg border-[0.4px] border-warning-500 bg-warning-50 py-5 sm:px-6"
						>
							<img
								alt="excel icon"
								class="text-xs"
								:height="80"
								:width="80"
								src="@/assets/images/png/excel-icon.png"
							/>

							<button
								type="button"
								:disabled="downloading"
								aria-controls="button"
								class="my-0 mr-4 flex cursor-pointer items-center gap-x-2 space-x-3 text-sm font-semibold text-gray-700 disabled:cursor-wait disabled:text-gray-400 sm:mr-0"
								@click="$emit('download')"
							>
								<lazy-tools-icon
									v-if="!downloading"
									dir="system"
									icon="download"
									path-class="stroke-gray-600"
								/>
								<lazy-tools-icon v-else dir="line" icon="eclipse" path-class="stroke-gray-600" />
								Download
							</button>
						</div>
					</template>
					<template v-if="value.key === 'details'">
						<div class="relative flex items-end gap-3">
							<img :width="200" src="/images/jpg/csv_template.jpg" loading="lazy" alt="template" />
							<p
								class="cursor-pointer text-sm font-medium text-primary-2"
								@click="() => previewImage(true)"
							>
								Preview
							</p>
							<div class="hidden">
								<a-image-preview-group
									:preview="{
										visible: isPreviewingImage,
										onVisibleChange: previewImage
									}"
								>
									<a-image :src="props.previewImgSrc" />
								</a-image-preview-group>
							</div>
						</div>
					</template>
					<template v-if="value.key === 'upload'">
						<shared-upload-input
							class="w-full"
							:is-document="true"
							:accept="fileFormat || '.xlsx'"
							@change="initFile"
						/>
					</template>
				</div>
			</div>
		</div>

		<slot name="available-file" />

		<dialogs-error
			v-model:show="isCsvError"
			:icon-props="{
				dir: 'fill',
				icon: 'warning-2'
			}"
			modal-button-text="Re-upload File"
			header="Upload Failed"
			:use-cancel-btn="false"
			@done="
				() => {
					reUpload = true
					isCsvError = false
				}
			"
		>
			<div class="mx-auto min-h-[100px] w-full max-w-md break-words rounded-md bg-gray-100 p-5 text-gray-600">
				Out of the
				<span class="font-medium"> {{ errorMessage?.totalRows }} rows </span>
				in the file, there are errors in
				<span class="font-medium text-error-500">
					row: {{ errorMessage?.row.join(',') }}, column: '{{ errorMessage?.column.join(',') }}' </span
				>. Please review the errors on the file and and re-upload it.
			</div>
		</dialogs-error>
	</div>
</template>

<style scoped>
	.line {
		@apply after:z-[5] after:h-[65%] after:translate-x-[25px] after:border-l-[3px] after:border-dotted after:border-gray-200 last:after:h-[0] after:sm:absolute after:sm:h-[60%];
	}
	.line:not(:first-child)::after {
		@apply h-0;
	}
</style>
